import Client from "@walletconnect/sign-client";
import { getAppMetadata, getSdkError } from "@walletconnect/utils";
import { PairingTypes, SessionTypes } from "@walletconnect/types";
import { TransactionBCH, Input, Output, stringify } from "@bitauth/libauth";
import { Web3Modal } from "@web3modal/standalone";
import EventEmitter from "events";
import { AbiFunction, Artifact } from "cashscript";

declare global {
  var paytaca: IConnector | undefined
}

export type ConnectorType = "WalletConnectV2" | "Paytaca" | null;

export interface ContractInfo {
  contract?: {
    abiFunction: AbiFunction;
    redeemScript: Uint8Array;
    artifact: Partial<Artifact>;
  }
}

export interface IConnector {
  ready: () => Promise<boolean>;
  address: () => Promise<string | undefined>;
  signTransaction: (options: {transaction: string | TransactionBCH, sourceOutputs: (Input | Output | ContractInfo)[], broadcast?: boolean, userPrompt?: string}) => Promise<{ signedTransaction: string, signedTransactionHash: string}>;
  signMessage: (options: {message: string, userPrompt?: string}) => Promise<string | undefined>;
  connect: () => Promise<void>;
  connected: () => Promise<boolean>;
  disconnect: () => Promise<void>;
  on(event: string, callback: Function): void;
  on(event: "addressChanged", callback: Function): void;
  on(event: "disconnect", callback: Function): void;
};

// paytaca
export class PaytacaConnector implements IConnector {
  async ready(): Promise<boolean> {
    return new Promise((resolve) => {
      let retries = 0;
      const interval = setInterval(() => {
        retries++;
        if (retries > 30) {
          clearInterval(interval);
          resolve(false);
        }

        if (window.paytaca) {
          clearInterval(interval);
          resolve(true);
        }
      }, 50);
    });
  };

  async address(): Promise<string | undefined> {
    return window.paytaca?.address();
  };
  async signTransaction(options: { transaction: string | TransactionBCH; sourceOutputs: (Input<Uint8Array, Uint8Array> | Output<Uint8Array, Uint8Array> | ContractInfo)[]; broadcast?: boolean; userPrompt?: string | undefined; }): Promise<{ signedTransaction: string, signedTransactionHash: string}> {
    return window.paytaca?.signTransaction(options)!;
  };
  async signMessage(options: { message: string; userPrompt?: string | undefined; }): Promise<string | undefined> {
    return window.paytaca?.signMessage(options);
  };
  async connect(): Promise<void> {
    return window.paytaca?.connect();
  };
  async connected(): Promise<boolean> {
    return !!(await window.paytaca?.connected());
  };
  async disconnect(): Promise<void> {
    return window.paytaca?.disconnect();
  };
  on(event: string, callback: Function): void {
    return window.paytaca?.on(event, callback);
  };
}

// walletconnect 2
export const DEFAULT_PROJECT_ID = "3fd234b8e2cd0e1da4bc08a0011bbf64"; //process.env.NEXT_PUBLIC_PROJECT_ID;
export const DEFAULT_RELAY_URL = process.env.NEXT_PUBLIC_RELAY_URL;

// export const DEFAULT_LOGGER = "debug";
export const DEFAULT_LOGGER = undefined;

export const DEFAULT_APP_METADATA = {
  name: "Slime Shadies",
  description: "Slime Shadies Blockchain Game",
  url: "https://game.shadybattlegrounds.xyz/",
  icons: ["https://game.shadybattlegrounds.xyz/logo512.png"],
};

const web3Modal = new Web3Modal({
  projectId: DEFAULT_PROJECT_ID!,

  walletConnectVersion: 2,
  desktopWallets: [{
    id: "Cashonize",
    name: "Cashonize Beta",
    links: {
      native: undefined as any,
      universal: "https://cashonize-wc2-beta.vercel.app/#"
    },
  }],
  walletImages: {
    "Cashonize": "https://cashonize.com/images/cashonize-icon.png"
  },
  enableExplorer: false,
  enableAccountView: true,
  mobileWallets: [],
  explorerRecommendedWalletIds: "NONE",
});

const globalClient = await Client.init({
  logger: DEFAULT_LOGGER,
  relayUrl: DEFAULT_RELAY_URL!,
  projectId: DEFAULT_PROJECT_ID,
  metadata: DEFAULT_APP_METADATA || getAppMetadata(),
});

export class WalletConnect2Connector implements IConnector {
  client?: Client;
  pairings: PairingTypes.Struct[] = [];
  session?: SessionTypes.Struct = undefined;
  prevRelayerValue: string = "";
  accounts: string[] = [];
  chains: string[] = [];
  relayerRegion: string = DEFAULT_RELAY_URL!;
  events: EventEmitter = new EventEmitter();

  constructor() {
  };

  reset() {
    this.session = undefined;
    this.accounts = [];
    this.chains = [];
    this.relayerRegion = DEFAULT_RELAY_URL!;
    this.client?.removeAllListeners(undefined as any);
  };

  async ready(): Promise<boolean> {
    return true;
  }

  async onSessionConnected(_session: SessionTypes.Struct) {
    const allNamespaceAccounts = Object.values(_session.namespaces)
      .map((namespace) => namespace.accounts)
      .flat();

    this.session = _session;
    const isDevelopmentMode = process.env.REACT_APP_WEBGL_RESOURCE_KEY !== "Prod";
    this.chains = isDevelopmentMode ? ["bch:bchtest"] : ["bch:bitcoincash"];
    this.accounts = allNamespaceAccounts;
  }

  async _subscribeToEvents(_client: Client) {
    if (typeof _client === "undefined") {
      throw new Error("WalletConnect is not initialized");
    }

    _client.on("session_ping", (args) => {
      DEFAULT_LOGGER && console.log("EVENT", "session_ping", args);
    });

    _client.on("session_event", (args) => {
      DEFAULT_LOGGER && console.log("EVENT", "session_event", args);
      const params = args.params;
      if (params.chainId !== this.chains[0]) {
        return;
      }

      this.events.emit(params.event.name, params.event.data);
    });

    _client.on("session_update", ({ topic, params }) => {
      DEFAULT_LOGGER && console.log("EVENT", "session_update", { topic, params });
      const { namespaces } = params;
      const _session = _client.session.get(topic);
      const updatedSession = { ..._session, namespaces };
      this.onSessionConnected(updatedSession);
    });

    _client.on("session_delete", (args) => {
      DEFAULT_LOGGER && console.log("EVENT", "session_delete");
      this.events.emit("disconnect", args);
      this.reset();
    });
  };

  async _checkPersistedState(_client: Client) {
    if (typeof _client === "undefined") {
      throw new Error("WalletConnect is not initialized");
    }
    // populates existing pairings to state
    this.pairings = _client.pairing.getAll({ active: true });
    DEFAULT_LOGGER && console.log(
      "RESTORED PAIRINGS: ",
      _client.pairing.getAll({ active: true })
    );

    if (typeof this.session !== "undefined") return;
    // populates (the last) existing session to state
    if (_client.session.length) {
      const lastKeyIndex = _client.session.keys.length - 1;
      const _session = _client.session.get(
        _client.session.keys[lastKeyIndex]
      );
      DEFAULT_LOGGER && console.log("RESTORED SESSION:", _session);
      await this.onSessionConnected(_session);
      return _session;
    }
  };

  async address(): Promise<string | undefined> {
    try {
      const result = await this.client!.request<string[]>({
        chainId: this.chains[0],
        topic: this.session!.topic,
        request: {
          method: "bch_getAddresses",
          params: {},
        },
      });

      return result[0];
    } catch (error: any) {
      return undefined;
    }
  };

  async signTransaction(options: { transaction: string | TransactionBCH; sourceOutputs: (Input<Uint8Array, Uint8Array> | Output<Uint8Array, Uint8Array> | ContractInfo)[]; broadcast?: boolean; userPrompt?: string | undefined; }): Promise<{ signedTransaction: string, signedTransactionHash: string}> {
    setTimeout(() => alert(`Approve transaction in ${this.session?.peer.metadata.name}`), 100);
    const result = await this.client!.request<{ signedTransaction: string, signedTransactionHash: string}>({
      chainId: this.chains[0],
      topic: this.session!.topic,
      request: {
        method: "bch_signTransaction",
        params: JSON.parse(stringify(options)),
      },
    });

    return result;
  };

  async signMessage(options: { message: string; userPrompt?: string | undefined; }): Promise<string | undefined> {
    try {
      setTimeout(() => alert(`Sign message in ${this.session?.peer.metadata.name}`), 100);
      const result = await this.client!.request<string>({
        chainId: this.chains[0],
        topic: this.session!.topic,
        request: {
          method: "bch_signMessage",
          params: options,
        },
      });

      return result;
    } catch (error: any) {
      return undefined;
    }
  };

  async connect(): Promise<void> {
    this.client = globalClient;

    this.prevRelayerValue = this.relayerRegion;
    await this._subscribeToEvents(this.client);
    await this._checkPersistedState(this.client);

    if (!this.session) {
      const pairings = this.client.pairing.getAll({ active: true });
      this.pairings = pairings;

      await this._connect(pairings[0]);
    }
  };

  async _connect(pairing: any) {
    if (typeof this.client === "undefined") {
      throw new Error("WalletConnect is not initialized");
    }
    DEFAULT_LOGGER && console.log("connect, pairing topic is:", pairing?.topic);
    const isDevelopmentMode = process.env.REACT_APP_WEBGL_RESOURCE_KEY !== "Prod";
    try {
      const requiredNamespaces = {
        "bch": {
            "methods": [
                "bch_getAddresses",
                "bch_signTransaction",
                "bch_signMessage"
            ],
            "chains": isDevelopmentMode ? ["bch:bchtest"] : ["bch:bitcoincash"],
            "events": [
                "addressesChanged"
            ]
        }
      };
      DEFAULT_LOGGER && console.log(
        "requiredNamespaces config for connect:",
        requiredNamespaces
      );

      const { uri, approval } = await this.client.connect({
        pairingTopic: pairing?.topic,
        requiredNamespaces,
      });

      // Open QRCode modal if a URI was returned (i.e. we're not connecting an existing pairing).
      if (uri) {
        // Create a flat array of all requested chains across namespaces.
        const standaloneChains = Object.values(requiredNamespaces)
          .map((namespace) => namespace.chains)
          .flat() as string[];

        web3Modal.openModal({ uri, standaloneChains });
      }

      if (!uri) {
        setTimeout(() => alert(`Approve session in ${pairing?.peerMetadata.name}`), 100);
      }
      const session = await approval();
      DEFAULT_LOGGER && console.log("Established session:", session);
      await this.onSessionConnected(session);
      // Update known pairings after session is connected.
      this.pairings = this.client.pairing.getAll({ active: true });
    } catch (e) {
      console.error(e);
      // ignore rejection
    } finally {
      // close modal in case it was open
      web3Modal.closeModal();
    }
  }

  async connected(): Promise<boolean> {
    return this.client !== undefined && this.session !== undefined;
  };
  async disconnect(): Promise<void> {
    if (typeof this.client === "undefined") {
      throw new Error("WalletConnect is not initialized");
    }
    if (typeof this.session === "undefined") {
      throw new Error("Session is not connected");
    }

    try {
      await this.client.disconnect({
        topic: this.session.topic,
        reason: getSdkError("USER_DISCONNECTED"),
      });
    } catch (error) {
      DEFAULT_LOGGER && console.error("SignClient.disconnect failed:", error);
    } finally {
      // Reset app state after disconnect.
      this.reset();
    }
  };
  on(event: string, callback: Function): void {
    this.events.on(event, callback as any);
  }
}
